import { useState } from 'react';
import { BarChart3, Table2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface ChartPanelProps {
  title: string;
  description?: string;
  /** Right-of-title soft badge (e.g. a "2 need action" alert), or a live chip. */
  headerBadge?: React.ReactNode;
  /** The Recharts view. */
  chart: React.ReactNode;
  /** The accessible data-table twin (mandatory relief channel per the dataviz conventions). */
  table: React.ReactNode;
  className?: string;
}

/**
 * Chart card with the house per-chart table toggle (86d3peur0 Phase C). Every trend chart has an accessible
 * table twin; the grid/table icon button swaps between them. Icon-only control → carries a Tooltip + aria-label.
 */
export function ChartPanel({ title, description, headerBadge, chart, table, className }: ChartPanelProps) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const showingTable = view === 'table';

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-2">
        <div className="min-w-0 space-y-1">
          <CardTitle className="text-base">{title}</CardTitle>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {headerBadge}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={showingTable ? 'Show chart' : 'Show data table'}
                onClick={() => setView(showingTable ? 'chart' : 'table')}
              >
                {showingTable ? <BarChart3 className="h-4 w-4" /> : <Table2 className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{showingTable ? 'Show chart' : 'Show data table'}</TooltipContent>
          </Tooltip>
        </div>
      </CardHeader>
      <CardContent>{showingTable ? table : chart}</CardContent>
    </Card>
  );
}
